/**
 * Creates a type-safe path proxy for react-hook-form nested paths
 *
 * @example
 * const path = createPathProxy<Step1FormData>();
 * <ControlledTextField name={String(path.student.basicDetails.firstName)} />
 */
export function createPathProxy<T extends Record<string, any>>(): PathProxy<T> {
  return createProxy<T>([]);
}

type PathProxy<T> = PathString & {
  [K in keyof Required<T>]: PathValue<NonNullable<Required<T>[K]>>;
};

type PathValue<V> = V extends Record<string, any> ? PathProxy<V> : PathString;

type PathString = {
  toString(): string;
  valueOf(): string;
};

function createProxy<T>(path: string[]): PathProxy<T> {
  const pathString = path.join('.');

  const proxyTarget = {
    toString: () => pathString,
    valueOf: () => pathString,
  };

  return new Proxy(proxyTarget as PathProxy<T>, {
    get(target, prop: string | symbol) {
      if (prop === 'toString' || prop === 'valueOf') {
        return target[prop as keyof typeof target];
      }

      if (prop === Symbol.toPrimitive) {
        return () => pathString;
      }

      const newPath = [...path, prop as string];
      return createProxy(newPath);
    },
  }) as PathProxy<T>;
}
